/**
 * Carpool document uploads (254g): licence / ID photos, licence disc, insurance, child-safety
 * clearance. Which kinds are asked — and how strongly — comes from GET me/documents.
 *
 * Files never pass through the Carpool API: upload-url issues a one-time signed PUT to PRIVATE
 * storage, the browser PUTs the bytes with exactly the ticket's headers (no Authorization), and
 * confirm asks the server to check what landed. Photos are decoded, resized to 2000 px on the long
 * side and re-encoded as JPEG on the device first — which strips EXIF (GPS included). PDFs go as-is,
 * only for types that allow them. View links are short-lived and are never cached.
 */
import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useRidesMeService,
  RIDES_ERROR_CODES,
  RIDES_UPLOAD_CONTENT_TYPES,
  RIDES_UPLOAD_STATUS,
  type RidesMyUploadsView,
  type RidesUploadTypeView,
  type RidesUploadView,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { ridesKeys } from './queryKeys';
import { errorMessage, hasErrorCode, isNotFoundError } from './copy';
import { encodeImageAsJpeg, looksLikeImage, PhotoError } from './photo';
import { documentRules, requirementLevel, strongerLevel, useRidesSettings, type RequirementLevel } from './settings';

export const DOC_MAX_EDGE = 2000;
export const DOC_QUALITY = 0.85;

/** Which member surface is asking: driver steps, rider booking, or the Account page (both). */
export type DocumentContext = 'driver' | 'rider' | 'all';

// ---------------------------------------------------------------- errors

export type DocumentErrorCode = 'not_image' | 'pdf_not_allowed' | 'not_pdf' | 'too_large' | 'decode_failed' | 'upload_failed';

export class DocumentError extends Error {
  readonly code: DocumentErrorCode;
  constructor(code: DocumentErrorCode, message: string) {
    super(message);
    this.name = 'DocumentError';
    this.code = code;
  }
}

/** Friendly text for anything thrown while preparing / uploading / deleting a document. */
export function documentErrorMessage(err: unknown, fallback = "We couldn't upload that document. Please try again."): string {
  if (err instanceof DocumentError) return err.message;
  return errorMessage(err, fallback);
}

// ---------------------------------------------------------------- prepare

export interface PreparedDocument {
  blob: Blob;
  contentType: string;
  isPdf: boolean;
}

export function isPdfType(contentType: string | null | undefined): boolean {
  return (contentType ?? '').toLowerCase().includes('pdf');
}

function looksLikePdf(file: File): boolean {
  return isPdfType(file.type) || /\.pdf$/i.test(file.name);
}

async function startsWithPdfMagic(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 5).arrayBuffer());
    return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46; // %PDF
  } catch {
    return true; // let the server's byte check decide
  }
}

/**
 * Photo → resized JPEG (strips EXIF/GPS); PDF → as-is when `allowPdf`. Enforces `maxMb` on what
 * will actually be uploaded.
 */
export async function prepareDocument(file: File, opts: { maxMb: number; allowPdf: boolean }): Promise<PreparedDocument> {
  const { maxMb, allowPdf } = opts;
  if (looksLikePdf(file)) {
    if (!allowPdf) throw new DocumentError('pdf_not_allowed', 'This one needs to be a photo (JPG, PNG or HEIC), not a PDF. You can take a photo of the document instead.');
    if (file.size > maxMb * 1024 * 1024) {
      throw new DocumentError('too_large', `That PDF is larger than ${maxMb} MB. Please choose a smaller file, or take a photo of the page instead.`);
    }
    if (!(await startsWithPdfMagic(file))) throw new DocumentError('not_pdf', "That file doesn't look like a PDF. Please choose another file.");
    return { blob: file.slice(0, file.size, RIDES_UPLOAD_CONTENT_TYPES.Pdf), contentType: RIDES_UPLOAD_CONTENT_TYPES.Pdf, isPdf: true };
  }
  if (!looksLikeImage(file)) {
    throw new DocumentError('not_image', allowPdf ? 'Please choose a photo (JPG, PNG or HEIC) or a PDF.' : 'Please choose a photo (JPG, PNG or HEIC).');
  }
  try {
    const prepared = await encodeImageAsJpeg(file, {
      maxEdge: DOC_MAX_EDGE,
      quality: DOC_QUALITY,
      maxMb,
      fileName: `document-${Date.now()}.jpg`,
      noun: 'photo',
    });
    return { blob: prepared.blob, contentType: prepared.mimeType, isPdf: false };
  } catch (e) {
    if (e instanceof PhotoError) throw new DocumentError(e.code === 'too_large' ? 'too_large' : e.code === 'not_image' ? 'not_image' : 'decode_failed', e.message);
    throw e;
  }
}

// ---------------------------------------------------------------- PUT with progress

/** Headers the browser sets itself (setting them throws or is ignored). */
const FORBIDDEN_HEADERS = new Set(['host', 'content-length', 'connection', 'accept-encoding', 'cookie', 'origin', 'referer']);

/** PUT the prepared bytes to the signed URL with exactly the ticket's headers; reports 0–1 progress. */
function putToStorage(
  url: string,
  method: string,
  headers: Record<string, string> | null | undefined,
  blob: Blob,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method || 'PUT', url, true);
    for (const [name, value] of Object.entries(headers ?? {})) {
      if (!FORBIDDEN_HEADERS.has(name.toLowerCase()) && value !== undefined && value !== null) xhr.setRequestHeader(name, String(value));
    }
    xhr.timeout = 5 * 60_000;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new DocumentError('upload_failed', "The upload didn't go through. Please try again."));
    };
    xhr.onerror = () => reject(new DocumentError('upload_failed', "The upload didn't go through. Check your connection and try again."));
    xhr.ontimeout = () => reject(new DocumentError('upload_failed', 'The upload took too long. Check your connection and try again.'));
    xhr.send(blob);
  });
}

// ---------------------------------------------------------------- queries

/**
 * GET me/documents — the types asked of me, my uploads and what is missing. Disabled (and null)
 * when uploads are off or storage isn't configured; an API without the endpoint (404) or without
 * storage (503 document_storage_unavailable) also resolves to null, which hides the area.
 */
export function useMyDocuments() {
  const me = useRidesMeService();
  const { isAuthenticated } = useEnrichedAuth();
  const { settings } = useRidesSettings();
  const enabled = documentRules(settings).enabled;
  return useQuery<RidesMyUploadsView | null>({
    queryKey: ridesKeys.me.documents,
    queryFn: async () => {
      try {
        return await me.getDocuments();
      } catch (e) {
        if (isNotFoundError(e) || hasErrorCode(e, RIDES_ERROR_CODES.DocumentStorageUnavailable)) return null;
        throw e;
      }
    },
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

/** The view is present and the server says uploads are on and storage is ready. */
export function docsUsable(view: RidesMyUploadsView | null | undefined): view is RidesMyUploadsView {
  return !!view && view.enabled !== false && view.storageReady !== false;
}

// ---------------------------------------------------------------- derived helpers

export interface LevelOptions {
  /** 'all' context: include driver requirements (the member has a driver profile). */
  isDriver?: boolean;
  /** settings.minorRidesRequireClearedDriver — clearance types are offered to drivers (optional). */
  clearanceForDrivers?: boolean;
}

/** How strongly a type is asked in this context ('notAsked' = hide). */
export function levelFor(type: RidesUploadTypeView, context: DocumentContext, opts: LevelOptions = {}): RequirementLevel {
  let driver = requirementLevel(type.driverRequirementID);
  if (type.isChildSafetyClearance && opts.clearanceForDrivers) driver = strongerLevel(driver, 'optional');
  const rider = requirementLevel(type.riderRequirementID);
  if (context === 'driver') return driver;
  if (context === 'rider') return rider;
  return opts.isDriver ? strongerLevel(driver, rider) : rider;
}

/** Active types asked in this context, in the admin's sort order. */
export function typesFor(view: RidesMyUploadsView, context: DocumentContext, opts: LevelOptions = {}): RidesUploadTypeView[] {
  return (view.types ?? [])
    .filter((t) => t.isActive !== false && levelFor(t, context, opts) !== 'notAsked')
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
}

const LIVE = [RIDES_UPLOAD_STATUS.Submitted, RIDES_UPLOAD_STATUS.Accepted] as number[];

/** Newest first: by upload stamp (yyyyMMddHHmmss), then by id. */
function newestFirst(a: RidesUploadView, b: RidesUploadView): number {
  return Number(b.uploadedAt ?? 0) - Number(a.uploadedAt ?? 0) || b.rideUploadID - a.rideUploadID;
}

/** The upload that represents this type: the live one, else the latest rejected / expired one. */
export function currentUpload(view: RidesMyUploadsView | null | undefined, typeId: number): RidesUploadView | null {
  const list = (view?.uploads ?? [])
    .filter((u) => u.uploadTypeID === typeId && u.statusID !== RIDES_UPLOAD_STATUS.Withdrawn && u.statusID !== RIDES_UPLOAD_STATUS.Uploading)
    .sort(newestFirst);
  return list.find((u) => LIVE.includes(u.statusID)) ?? list[0] ?? null;
}

/** Submitted / Accepted and not past its expiry date. */
export function isLiveUpload(u: RidesUploadView | null | undefined): u is RidesUploadView {
  return !!u && LIVE.includes(u.statusID) && !u.isExpired;
}

/** Missing (Required, not satisfied) type ids for this context. */
export function missingIdsFor(view: RidesMyUploadsView, context: DocumentContext, isDriver = false): number[] {
  const driver = view.missingForDriver ?? [];
  const rider = view.missingForRider ?? [];
  if (context === 'driver') return driver;
  if (context === 'rider') return rider;
  return Array.from(new Set([...(isDriver ? driver : []), ...rider]));
}

export interface MissingSplit {
  /** Nothing usable uploaded yet (or rejected / expired) — the member must act. */
  toUpload: RidesUploadTypeView[];
  /** Uploaded and waiting for a reviewer (documentApprovalRequired). */
  awaitingReview: RidesUploadTypeView[];
}

export function splitMissing(view: RidesMyUploadsView, ids: number[]): MissingSplit {
  const out: MissingSplit = { toUpload: [], awaitingReview: [] };
  for (const id of ids) {
    const type = view.types?.find((t) => t.uploadTypeID === id) ?? null;
    const named: RidesUploadTypeView = type ?? ({ uploadTypeID: id, name: 'A required document' } as RidesUploadTypeView);
    const u = currentUpload(view, id);
    if (u && u.statusID === RIDES_UPLOAD_STATUS.Submitted && !u.isExpired) out.awaitingReview.push(named);
    else out.toUpload.push(named);
  }
  return out;
}

/** "Driver's licence (front), ID or passport" */
export function typeNames(types: Pick<RidesUploadTypeView, 'name'>[]): string {
  return types.map((t) => t.name).join(', ');
}

/** Names for type ids (from the documents view), falling back to a neutral label. */
export function namesForIds(view: RidesMyUploadsView | null | undefined, ids: number[] | null | undefined): string {
  return (ids ?? [])
    .map((id) => view?.types?.find((t) => t.uploadTypeID === id)?.name ?? 'a required document')
    .join(', ');
}

// ---------------------------------------------------------------- actions

export type DocumentStage = 'idle' | 'preparing' | 'uploading' | 'checking' | 'deleting';

export const DOCUMENT_STAGE_TEXT: Record<DocumentStage, string> = {
  idle: '',
  preparing: 'Preparing your file…',
  uploading: 'Uploading privately…',
  checking: 'Checking the file…',
  deleting: 'Deleting…',
};

export interface UploadOptions {
  allowPdf: boolean;
  maxMb: number;
  /** yyyy-MM-dd (askExpiryDate types). */
  expiresAt?: string | null;
  /** Child-safety clearance consent (docs.clearance_consent). */
  consentGranted?: boolean | null;
}

export interface DocumentActions {
  stage: DocumentStage;
  busy: boolean;
  /** 0–100 */
  progress: number;
  error: string | null;
  upload: (typeId: number, file: File, opts: UploadOptions) => Promise<RidesUploadView | null>;
  remove: (rideUploadId: number) => Promise<boolean>;
  clearError: () => void;
}

/**
 * Upload / delete one document with stage + progress + inline error (one instance per document
 * row). On success everything "about me" is refetched — the documents view, the driver profile
 * (documentsComplete / canList) and the summary.
 */
export function useDocumentActions(): DocumentActions {
  const me = useRidesMeService();
  const qc = useQueryClient();
  const [stage, setStage] = useState<DocumentStage>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ridesKeys.me.all });
  }, [qc]);

  const upload = useCallback(
    async (typeId: number, file: File, opts: UploadOptions) => {
      setError(null);
      setStage('preparing');
      setProgress(5);
      let reachedServer = false;
      try {
        const prepared = await prepareDocument(file, { maxMb: opts.maxMb, allowPdf: opts.allowPdf });
        setProgress(15);
        reachedServer = true;
        const ticket = await me.createDocumentUpload(typeId, {
          contentType: prepared.contentType,
          sizeBytes: prepared.blob.size,
          expiresAt: opts.expiresAt ?? null,
          consentGranted: opts.consentGranted ?? null,
        });
        setStage('uploading');
        setProgress(20);
        await putToStorage(ticket.uploadUrl, ticket.method, ticket.headers, prepared.blob, (f) => setProgress(20 + Math.round(f * 65)));
        setStage('checking');
        setProgress(90);
        const saved = await me.confirmDocument(ticket.rideUploadID);
        setProgress(100);
        refresh();
        return saved;
      } catch (e) {
        setError(documentErrorMessage(e));
        // A rejected confirm (document_invalid) deletes the file server-side; refresh what we show.
        if (reachedServer) refresh();
        return null;
      } finally {
        setStage('idle');
        setProgress(0);
      }
    },
    [me, refresh],
  );

  const remove = useCallback(
    async (rideUploadId: number) => {
      setError(null);
      setStage('deleting');
      try {
        await me.deleteDocument(rideUploadId);
        refresh();
        return true;
      } catch (e) {
        setError(documentErrorMessage(e, "We couldn't delete that document. Please try again."));
        return false;
      } finally {
        setStage('idle');
      }
    },
    [me, refresh],
  );

  return {
    stage,
    busy: stage !== 'idle',
    progress,
    error,
    upload,
    remove,
    clearError: useCallback(() => setError(null), []),
  };
}
